import React, { Component } from "react";
import {
  loading,
  NavbarAdmin,
  GET,
  GET_FILE,
  DateRangeFields,
  Select,
  alert,
  saveAs,
  currentLang,
  t,
} from "../../components/CustomComponent.js";
import { dateToKey, keyToDate } from "../../components/DateRangeFields.js";

// หน้ารายงานช่าง - หน้าเดียว มีแท็บรายงานอยู่ด้านซ้าย กดแล้วสลับรายงานทางขวา
// ข้อมูลดึงจาก admin/technician-reports/<type> (SNAPA-Backend modules/admin/technician-report)
//   GET .../technicians            -> ตัวเลือกช่าง
//   GET .../<type>                 -> { title, columns, rows, summary, filter }
//   GET .../<type>/export?format=  -> ไฟล์ Excel/PDF (GET_FILE เพราะเป็น binary)
const REPORT_TABS = [
  { type: "job-history", icon: "", name: "tr_job_history" },
  { type: "time", icon: "", name: "tr_time" },
  { type: "parts-usage", icon: "", name: "tr_parts_usage" },
  { type: "travel", icon: "", name: "tr_travel" },
  { type: "customer-feedback", icon: "", name: "tr_customer_feedback" },
];

// ความสูงขั้นต่ำของกล่องตาราง (ส่วนบนสูงมากก็ไม่ให้เล็กกว่านี้ หน้าเลื่อนเองแทน) · ช่องว่างใต้กล่อง = padding card + body
const TABLE_MIN_HEIGHT = 260;
const TABLE_BOTTOM_GAP = 56;
// offset ของ NavbarAdmin (fixed สูง 50px)
const NAVBAR_HEIGHT = 50;
const CELL_HEAD_STYLE = { position: "sticky", top: 0, zIndex: 2, backgroundColor: "#fff", boxShadow: "inset 0 -1px 0 #dee2e6" };

export default class TechnicianReports extends Component {
  constructor(props) {
    super(props);
    const now = new Date();
    this.state = {
      report_type: "job-history",
      technicians: [],
      filter_technician_uid: "all",
      start_date: new Date(now.getFullYear(), now.getMonth(), 1),
      end_date: new Date(now.getFullYear(), now.getMonth() + 1, 0),
      report: null,
      exporting: "",
      table_max_height: 480,
      header_height: 0,
    };
    this.tableScrollRef = React.createRef();
    this.headerRef = React.createRef();
    this.topRef = React.createRef();
    this.measureFrame = null;
    this.resizeObserver = null;
  }

  // วัดความสูงกล่องตารางจากตำแหน่งจริงบนหน้า (ไม่ใช้ calc(100vh - ...) เพราะส่วนบนสูงไม่คงที่)
  // เรียกผ่าน requestAnimationFrame เสมอ ไม่งั้น ResizeObserver ขึ้น "loop completed" overlay บน dev server
  scheduleMeasure = () => {
    if (this.measureFrame) cancelAnimationFrame(this.measureFrame);
    this.measureFrame = requestAnimationFrame(() => {
      this.measureFrame = null;
      const el = this.tableScrollRef.current;
      if (!el) return;
      const docTop = el.getBoundingClientRect().top + window.scrollY;
      const nextHeight = Math.max(TABLE_MIN_HEIGHT, Math.floor(window.innerHeight - docTop - TABLE_BOTTOM_GAP));
      const nextHeader = this.headerRef.current ? this.headerRef.current.offsetHeight : 0;
      if (nextHeight !== this.state.table_max_height || nextHeader !== this.state.header_height) {
        this.setState({ table_max_height: nextHeight, header_height: nextHeader });
      }
    });
  };

  async componentDidMount() {
    window.addEventListener("resize", this.scheduleMeasure);
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(this.scheduleMeasure);
      if (this.headerRef.current) this.resizeObserver.observe(this.headerRef.current);
      if (this.topRef.current) this.resizeObserver.observe(this.topRef.current);
    }
    this.scheduleMeasure();
    loading(true);
    const result = await GET("admin/technician-reports/technicians");
    this.setState({ technicians: result?.status ? result.data || [] : [] });
    await this.loadReport();
    loading(false);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.report !== this.state.report) this.scheduleMeasure();
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.scheduleMeasure);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.measureFrame) cancelAnimationFrame(this.measureFrame);
  }

  // ฟิลเตอร์ที่ส่งให้ API - ใช้ทั้งตอนโหลดตารางและตอนสั่ง export ให้ได้ข้อมูลชุดเดียวกัน
  getQuery = () => ({
    start_date: this.toDateParam(this.state.start_date),
    end_date: this.toDateParam(this.state.end_date || this.state.start_date),
    technician_uid: this.state.filter_technician_uid || "all",
    lang: (localStorage.getItem("lang") || "TH") === "EN" ? "en" : "th",
  });

  // ป้ายชื่อช่างใน dropdown - มีรหัสก็ต่อท้ายให้แยกคนชื่อซ้ำได้
  technicianLabel = (technician) => (technician.code ? `${technician.name} (${technician.code})` : technician.name);

  // ตัวเลือกฟิลเตอร์ช่าง: หัวหน้าที่มีลูกน้อง (backend ติดธง is_my_subordinate มาให้) จะได้ตัวเลือก
  // "ลูกน้องของฉัน" ทั้งทีม และเลือกลูกน้องรายคนจากกลุ่มบนสุด - ถ้าไม่มีลูกน้องลิสต์เหมือนเดิมทุกประการ
  getTechnicianOptions = () => {
    const { technicians } = this.state;
    const myTeam = technicians.filter((technician) => technician.is_my_subordinate);
    const options = [{ value: "all", label: `${t("all_technicians")} (${technicians.length})` }];

    if (!myTeam.length) {
      return [...options, ...technicians.map((technician) => ({ value: technician.uid, label: this.technicianLabel(technician) }))];
    }

    return [
      ...options,
      { value: "my_team", label: `${t("my_team_technicians")} (${myTeam.length})` },
      ...myTeam.map((technician) => ({ value: technician.uid, label: this.technicianLabel(technician), group: t("my_team_technicians") })),
      ...technicians
        .filter((technician) => !technician.is_my_subordinate)
        .map((technician) => ({ value: technician.uid, label: this.technicianLabel(technician), group: t("other_technicians") })),
    ];
  };

  // ป้ายบอกว่าตารางที่เห็นอยู่กรองด้วยอะไร - my_team ครอบหลายคนจึงไม่มีชื่อช่างเดี่ยวจาก API
  getScopeLabel = () => {
    const { report } = this.state;
    if (report?.filter?.technician_uid === "my_team") {
      return `${t("my_team_technicians")} (${report?.filter?.technician_count ?? 0})`;
    }
    return report?.filter?.technician_name || t("all_technicians");
  };

  toDateParam = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (isNaN(date.getTime())) return "";
    const pad = (number) => String(number).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  };

  loadReport = async () => {
    const result = await GET(`admin/technician-reports/${this.state.report_type}`, this.getQuery());
    this.setState({ report: result?.status ? result.data : null });
  };

  changeReport = async (type) => {
    if (type === this.state.report_type) return;
    loading(true);
    this.setState({ report_type: type, report: null }, async () => {
      await this.loadReport();
      loading(false);
    });
  };

  changeFilter = async (patch) => {
    loading(true);
    this.setState(patch, async () => {
      await this.loadReport();
      loading(false);
    });
  };

  exportFile = async (format) => {
    this.setState({ exporting: format });
    const result = await GET_FILE(`admin/technician-reports/${this.state.report_type}/export`, {
      ...this.getQuery(),
      format,
    });
    this.setState({ exporting: "" });

    if (!result?.status) {
      alert("", "error", t("error"), result?.message || t("export_failed"));
      return;
    }

    const query = this.getQuery();
    const stamp = [query.start_date, query.end_date].filter(Boolean).join("_") || "all";
    saveAs(result.blob, `${this.state.report_type}_${stamp}.${format === "pdf" ? "pdf" : "xlsx"}`);
  };

  // ป้ายคอลัมน์มาจาก API พร้อมกันสองภาษา จึงเลือกตามภาษาที่ผู้ใช้เปิดอยู่โดยไม่ต้องเพิ่ม key แปลใหม่
  pickLabel = (item) => ((localStorage.getItem("lang") || "TH") === "EN" ? item.en || item.th : item.th || item.en);

  formatCell = (row, column) => {
    const value = row[column.key];
    if (value === null || value === undefined || value === "") return "-";
    if (column.type === "money") return Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (column.type === "number") {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return String(value);
      return numeric.toLocaleString("en-US", { maximumFractionDigits: 2 });
    }
    return String(value);
  };

  formatSummary = (item) => {
    // ชม.นาที (1.30 = 1 ชั่วโมง 30 นาที) backend ส่งข้อความสำเร็จรูปมาแล้ว ห้ามแปลงเป็นตัวเลขไม่งั้น 1.30 กลายเป็น 1.3
    if (item.suffix === "hm") return `${item.value ?? "0.00"} ${t("hours_short")}`;
    const value = Number(item.value);
    if (!Number.isFinite(value)) return item.value;
    const text = value.toLocaleString("en-US", { maximumFractionDigits: 2 });
    if (item.suffix === "money") return `฿${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (item.suffix === "hr") return `${text} ${t("hours_short")}`;
    if (item.suffix === "km") return `${text} ${t("km_short")}`;
    if (item.suffix === "star") return `${text} / 5`;
    return text;
  };

  render() {
    const { report, report_type, filter_technician_uid, start_date, end_date, exporting, table_max_height, header_height } = this.state;
    const activeTab = REPORT_TABS.find((tab) => tab.type === report_type);
    const rows = report?.rows || [];
    const columns = report?.columns || [];

    return (
      <div className="w-100 min-vh-100" style={{ backgroundColor: "#F4F7FE" }}>
        <NavbarAdmin />
        <div className="body p-4 mt-3">

          {/* ส่วนหัว (ชื่อหน้า + ตัวกรอง) ค้างใต้ navbar ตอนเลื่อน */}
          <div ref={this.headerRef} style={{ position: "sticky", top: NAVBAR_HEIGHT, zIndex: 20, backgroundColor: "#F4F7FE" }}>

          {/* หัวหน้าจอ + ปุ่ม Export */}
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
            <div>
              <h3 className="fw-bold mb-1 text-dark">{t("technician_reports")}</h3>
              <div className="text-secondary text-14">{t("technician_reports_desc")}</div>
            </div>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-white border px-3 py-2 fw-bold rounded-3 text-nowrap d-flex align-items-center"
                disabled={!!exporting}
                onClick={() => this.exportFile("excel")}
              >
                <span className="icon me-2">{"\uf019"}</span>
                {exporting === "excel" ? t("loading") : "Excel"}
              </button>
              <button
                type="button"
                className="btn btn-white border px-3 py-2 fw-bold rounded-3 text-nowrap d-flex align-items-center"
                disabled={!!exporting}
                onClick={() => this.exportFile("pdf")}
              >
                <span className="icon me-2">{"\uf019"}</span>
                {exporting === "pdf" ? t("loading") : "PDF"}
              </button>
            </div>
          </div>

          {/* ตัวกรองที่ใช้ร่วมกันทุกรายงาน */}
          <div className="bg-white rounded-4 shadow-sm p-4 mb-4">
            <div className="row g-3 align-items-end">
              <div className="col-md-4">
                <label className="form-label text-12 text-secondary mb-1">{t("technician")}</label>
                <Select
                  className="form-control w-100"
                  options={this.getTechnicianOptions()}
                  value={filter_technician_uid}
                  onChange={(value) => this.changeFilter({ filter_technician_uid: value || "all" })}
                  placeholder={t("filter_by_technician")}
                  dropdownAutoWidth
                />
              </div>
              <div className="col-md-5">
                <label className="form-label text-12 text-secondary mb-1">{t("date_range")}</label>
                <div className="d-flex gap-2">
                  <DateRangeFields
                    type={currentLang()}
                    className="form-control"
                    fieldStyle={{ flex: 1, minWidth: 0 }}
                    startPlaceholder={t("start_date")}
                    endPlaceholder={t("end_date")}
                    startValue={dateToKey(start_date)}
                    endValue={dateToKey(end_date)}
                    onChange={({ start, end }) => this.changeFilter({ start_date: keyToDate(start), end_date: keyToDate(end) })}
                  />
                </div>
              </div>
            </div>
          </div>

          </div>

          <div className="row g-3">

            {/* แท็บรายชื่อรายงานด้านซ้าย */}
            {/* ค้างใต้ส่วนหัวตอนเลื่อน (ความสูงส่วนหัววัดจริงเก็บใน header_height) */}
            <div className="col-lg-3" style={{ position: "sticky", top: NAVBAR_HEIGHT + header_height, alignSelf: "flex-start", zIndex: 10 }}>
              <div className="card border-0 rounded-4 shadow-sm p-3">
                <div className="text-secondary text-12 fw-bold mb-2 ps-2">{t("report_list")}</div>
                <div className="d-flex flex-column gap-1">
                  {REPORT_TABS.map((tab) => {
                    const isActive = tab.type === report_type;
                    return (
                      <button
                        key={tab.type}
                        type="button"
                        className={`btn text-start rounded-3 px-3 py-2 fw-bold d-flex align-items-center border-0 ${isActive ? "btn-primary" : "btn-white text-secondary bg-transparent"}`}
                        onClick={() => this.changeReport(tab.type)}
                      >
                        <span className="icon me-2">{tab.icon}</span>
                        {t(tab.name)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* รายงานที่เลือก */}
            <div className="col-lg-9">
              <div className="bg-white rounded-4 shadow-sm p-4">
                <div ref={this.topRef}>
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                  <h5 className="fw-bold mb-0">{report ? this.pickLabel(report.title) : t(activeTab.name)}</h5>
                  <div className="text-secondary text-14">
                    {this.getScopeLabel()} · {rows.length} {t("rows")}
                  </div>
                </div>

                {/* ตัวเลขสรุปของรายงานนั้น ๆ */}
                <div className="row g-2 mb-3">
                  {(report?.summary || []).map((item) => (
                    <div className="col-6 col-md-3 col-xl-2" key={item.key}>
                      <div className="rounded-3 p-3 h-100" style={{ backgroundColor: "#F4F7FE" }}>
                        <div className="text-secondary text-12 fw-bold text-truncate" title={this.pickLabel(item)}>{this.pickLabel(item)}</div>
                        {/* ยอดเงินบางใบยาวหลักสิบล้าน ถ้าไม่ตัดจะดันการ์ดสูงไม่เท่ากัน - ตัดท้ายแล้วโชว์เต็มใน tooltip */}
                        <div className="fw-bold text-primary text-16 text-truncate" title={this.formatSummary(item)}>{this.formatSummary(item)}</div>
                      </div>
                    </div>
                  ))}
                </div>
                </div>

                {/* ตารางข้อมูล - รายงานบางใบมีคอลัมน์เยอะ จึงให้เลื่อนแนวนอนได้ */}
                {/* ตารางกว้างกว่าจอ - บังคับความกว้างขั้นต่ำจากน้ำหนักคอลัมน์ที่ API ส่งมา
                    ให้เลื่อนแนวนอนแทนการหักคำลงบรรทัดใหม่ (เลขที่ใบงาน/ชื่อช่างอ่านยากเมื่อถูกหัก) */}
                <div
                  ref={this.tableScrollRef}
                  className="table-responsive"
                  style={{ overflowX: "auto", overflowY: "auto", maxHeight: table_max_height }}
                >
                  <table
                    className="table table-hover align-middle mb-0"
                    style={{ minWidth: columns.reduce((total, column) => total + (column.width || 16), 0) * 9 }}
                  >
                    <thead>
                      <tr className="text-dark fw-bold text-12 border-bottom border-light">
                        {columns.map((column) => (
                          <th
                            key={column.key}
                            style={CELL_HEAD_STYLE}
                            className={`py-3 border-0 text-nowrap ${["number", "money", "duration"].includes(column.type) ? "text-end" : ""}`}
                          >
                            {this.pickLabel(column)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 && (
                        <tr>
                          <td colSpan={columns.length || 1} className="text-center text-secondary py-5">
                            {t("no_data")}
                          </td>
                        </tr>
                      )}
                      {rows.map((row, index) => (
                        <tr key={`${row.visit_uid || index}-${index}`}>
                          {columns.map((column) => (
                            <td
                              key={column.key}
                              // คอลัมน์ข้อความยาว (ชื่ออะไหล่/ความเห็นลูกค้า) ปล่อยให้ตัดบรรทัดได้ ที่เหลือคงบรรทัดเดียว
                              className={`text-14 ${["number", "money", "duration"].includes(column.type) ? "text-end" : ""} ${column.type === "text" && (column.width || 0) >= 26 ? "" : "text-nowrap"}`}
                              style={column.type === "text" && (column.width || 0) >= 26 ? { minWidth: 220 } : {}}
                            >
                              {this.formatCell(row, column)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
